/**
 * AccountBulkUpdateModal
 *
 * Bulk-update existing Investor Accounts via CSV / XLSX.
 *
 * Match strategy (server-side):
 *   1. Account Ref No  (the unique account id — downloadable as a reference list)
 *   2. PAN              (fallback when Ref No is missing/wrong)
 *
 * Mode dropdown:
 *   • Incremental — only writes blank fields on the account; APPENDS new rows to list fields
 *     (additional contacts, class commitments). Existing data is never overwritten.
 *   • Replace     — any non-blank cell from the CSV overwrites the existing value; for list
 *     fields the whole list is overwritten with what's in the row. Blank cells are skipped
 *     (existing data preserved).
 *
 * Non-matching rows are SKIPPED with a warning — bulk-update never creates new accounts.
 */
import React, { useState, useRef } from 'react';
import { Upload, Download, X, CheckCircle, AlertCircle, AlertTriangle, Loader, RefreshCw } from 'lucide-react';
import { investorAccountAPI } from '../../services/api';

const AccountBulkUpdateModal = ({ open, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('replace');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadReferenceList = async () => {
    try {
      const response = await investorAccountAPI.downloadAccountReferenceList();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'investor_account_reference_list.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download reference list');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await investorAccountAPI.downloadBulkUpdateTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'investor_accounts_update_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download template');
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please choose a CSV / XLSX file first.');
      return;
    }
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const res = await investorAccountAPI.bulkUpdate(file, mode);
      setResult(res.data);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="account-bulk-update-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
            <RefreshCw size={18} className="text-blue-600" />
            Bulk Update Existing Investor Accounts
          </h2>
          <button onClick={() => { reset(); onClose(); }} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Mode picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Update mode</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className={`border rounded-lg p-3 cursor-pointer transition-colors ${mode === 'incremental' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="bulk-update-mode-incremental">
                <input
                  type="radio"
                  name="mode"
                  value="incremental"
                  checked={mode === 'incremental'}
                  onChange={() => setMode('incremental')}
                  className="hidden"
                />
                <div className="font-medium text-sm text-gray-900">Incremental (fill blanks)</div>
                <p className="text-xs text-gray-500 mt-0.5">Only writes when the existing value is blank. Appends to list fields (contacts, class commitments). Safe / non-destructive.</p>
              </label>
              <label className={`border rounded-lg p-3 cursor-pointer transition-colors ${mode === 'replace' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="bulk-update-mode-replace">
                <input
                  type="radio"
                  name="mode"
                  value="replace"
                  checked={mode === 'replace'}
                  onChange={() => setMode('replace')}
                  className="hidden"
                />
                <div className="font-medium text-sm text-gray-900">Replace (overwrite)</div>
                <p className="text-xs text-gray-500 mt-0.5">Any non-blank cell overwrites the existing value. List fields are replaced wholesale. Blank cells are left alone.</p>
              </label>
            </div>
          </div>

          {/* Helpful downloads */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <p className="text-sm text-blue-800">
              <strong>Step 1:</strong> Download the <strong>Account Reference List</strong> to copy the unique <code>Account Ref No</code> for each account you want to update. Paste it into the first column of the <strong>Update Template</strong> (different from the standard import template). Rows are matched by <strong>Account Ref No → PAN → unique Account Name</strong>. Non-matching / ambiguous rows are skipped.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleDownloadReferenceList}
                className="inline-flex items-center text-sm text-blue-700 font-medium hover:text-blue-900"
                data-testid="download-account-ref-list-btn"
              >
                <Download size={16} className="mr-1" />
                Download Account Reference List
              </button>
              <button
                onClick={handleDownloadTemplate}
                className="inline-flex items-center text-sm text-blue-700 font-medium hover:text-blue-900"
                data-testid="download-account-template-btn-bu"
              >
                <Download size={16} className="mr-1" />
                Download CSV Template
              </button>
            </div>
          </div>

          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload CSV or XLSX</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setError(null); setResult(null); }}
              className="block w-full text-sm border rounded-lg px-3 py-2"
              data-testid="bulk-update-file-input"
            />
            {file && (
              <p className="text-xs text-green-700 mt-1.5">
                <Upload size={12} className="inline mr-1" /> {file.name} ({Math.round(file.size / 1024)} KB)
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 inline-flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="border rounded-lg overflow-hidden" data-testid="bulk-update-result">
              <div className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 ${result.updated > 0 ? 'bg-green-50 text-green-800 border-b border-green-200' : 'bg-amber-50 text-amber-800 border-b border-amber-200'}`}>
                {result.updated > 0
                  ? <><CheckCircle size={16} /> Updated {result.updated} account{result.updated !== 1 ? 's' : ''} in <strong>{result.mode}</strong> mode</>
                  : <><AlertTriangle size={16} /> No accounts updated</>
                }
                {result.skipped > 0 && (
                  <span className="ml-auto text-amber-700">
                    {result.skipped} row{result.skipped !== 1 ? 's' : ''} skipped
                  </span>
                )}
              </div>
              {result.header_warnings && result.header_warnings.length > 0 && (
                <div className="px-4 py-3 text-xs border-b border-amber-200 bg-amber-50" data-testid="bulk-update-header-warnings">
                  <p className="font-medium text-amber-800 mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Template header issues:
                  </p>
                  <ul className="space-y-1 text-amber-700">
                    {result.header_warnings.map((hw, i) => (
                      <li key={i} data-testid={`header-warning-${i}`}>• {hw.message}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-amber-600 italic">
                    Download a fresh template above to ensure all columns are present.
                  </p>
                </div>
              )}
              {result.warnings && result.warnings.length > 0 && (
                <div className="px-4 py-3 text-xs">
                  <p className="font-medium text-amber-800 mb-1.5">Warnings:</p>
                  <ul className="space-y-1 text-amber-700 max-h-40 overflow-y-auto">
                    {result.warnings.map((w, i) => (
                      <li key={i}>
                        <strong>Row {w.row}:</strong> {w.reason}
                        {w.account_name && <span className="text-amber-500"> — {w.account_name}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.errors && result.errors.length > 0 && (
                <div className="px-4 py-3 text-xs border-t border-red-200 bg-red-50">
                  <p className="font-medium text-red-800 mb-1.5">Errors:</p>
                  <ul className="space-y-1 text-red-700 max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={i}><strong>Row {e.row}:</strong> {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={() => { reset(); onClose(); }}
            disabled={uploading}
            className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            data-testid="bulk-update-cancel"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleSubmit}
              disabled={!file || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="bulk-update-submit"
            >
              {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Updating…' : 'Run Bulk Update'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountBulkUpdateModal;
